import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Users, Search, Globe, Clock, User2, Activity } from 'lucide-react';
import { useSteamUser } from '../hooks/useSteamUser';
import { UserProfileHeader } from '../components/UserProfileHeader';

type SortOption = 'name' | 'status' | 'lastOnline';

export function UserFriendsPage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user, isLoading, error } = useSteamUser(username || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('status');

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getOnlineStatus = (personastate: number): { text: string; color: string } => {
    switch (personastate) {
      case 0:
        return { text: 'Offline', color: 'text-gray-500' };
      case 1:
        return { text: 'Online', color: 'text-green-500' };
      case 2:
        return { text: 'Busy', color: 'text-red-500' };
      case 3:
        return { text: 'Away', color: 'text-yellow-500' };
      case 4:
        return { text: 'Snooze', color: 'text-purple-500' };
      case 5:
        return { text: 'Looking to Trade', color: 'text-blue-500' };
      case 6:
        return { text: 'Looking to Play', color: 'text-indigo-500' };
      default:
        return { text: 'Unknown', color: 'text-gray-500' };
    }
  };

  const sortedFriends = useMemo(() => {
    if (!user?.friends) return [];

    const filteredFriends = user.friends.filter(friend =>
      friend.personaname.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return [...filteredFriends].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.personaname.localeCompare(b.personaname);
        case 'status':
          return b.personastate - a.personastate;
        case 'lastOnline':
          return (b.lastlogoff || 0) - (a.lastlogoff || 0);
        default:
          return 0;
      }
    });
  }, [user?.friends, sortBy, searchQuery]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-32 bg-gray-200 rounded-lg mb-6"></div>
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Failed to load user data'}</p>
          <button
            onClick={() => navigate(`/user/${username}`)}
            className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Return to Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate(`/user/${username}`)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Profile
        </button>

        <UserProfileHeader user={user} />

        <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Steam Friends
              </h1>
              <p className="text-gray-600">
                {user.friendCount === 0 
                  ? 'This profile\'s friends list is private'
                  : `Connected with ${user.friendCount} players`}
              </p>
            </div>
            {user.friendCount > 0 && (
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search friends..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="status">Sort by Status</option>
                  <option value="name">Sort by Name</option>
                  <option value="lastOnline">Sort by Last Online</option>
                </select>
              </div>
            )}
          </div>

          {user.friendCount === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">This profile's friends list is private</p>
              <p className="text-sm text-gray-500 mt-2">
                The user needs to make their friends list public to view this information
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedFriends.map((friend) => {
                const status = getOnlineStatus(friend.personastate);
                return (
                  <Link
                    key={friend.steamid}
                    to={`/user/${friend.steamid}`}
                    className="flex items-start gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all group"
                  >
                    <img
                      src={friend.avatarmedium}
                      alt={friend.personaname}
                      className="w-12 h-12 rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate group-hover:text-indigo-600 transition-colors">
                        {friend.personaname}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center gap-1.5 text-sm ${status.color}`}>
                          <Activity className="w-4 h-4" />
                          {status.text}
                        </span>
                      </div>
                      {friend.lastlogoff && friend.personastate === 0 && (
                        <p className="text-sm text-gray-500 mt-1">
                          Last online: {formatDate(friend.lastlogoff)}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {user.friendCount > 0 && sortedFriends.length === 0 && (
            <div className="text-center py-12">
              <User2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No friends found matching your search</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}